import { useNavigate } from "@tanstack/react-router";
import { ClipboardCheck, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { ResizableTh } from "@/components/shared/resizable-th";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { AuditListItem } from "@/hooks/use-audits";
import { useAudits, useDeleteAudit } from "@/hooks/use-audits";
import type { ResizableColumn } from "@/hooks/use-resizable-columns";
import { useResizableColumns } from "@/hooks/use-resizable-columns";
import {
  ACTIVE_AUDIT_STATUSES,
  AUDIT_STATUS_BADGE_VARIANTS,
  AUDIT_STATUS_LABELS,
} from "@/lib/audit-status";
import { useSession } from "@/lib/auth-client";
import { formatDate } from "@/lib/format";
import { CreateAuditDialog } from "./CreateAuditDialog";
import { StaleAuditFlag } from "./StaleAuditFlag";

function ProgressBar({ checked, total }: { checked: number; total: number }) {
  const pct = total > 0 ? Math.round((checked / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className="h-full bg-primary rounded-full transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs text-muted-foreground tabular-nums w-8 text-right">{pct}%</span>
    </div>
  );
}

function matchesStatusFilter(auditStatus: string, filter: string | undefined): boolean {
  if (!filter) return true;
  if (filter === "active")
    return (ACTIVE_AUDIT_STATUSES as readonly string[]).includes(auditStatus);
  return auditStatus === filter;
}

export function AuditsList({
  statusFilter,
  searchQuery = "",
  showCreateButton = true,
}: {
  statusFilter?: string;
  searchQuery?: string;
  showCreateButton?: boolean;
}) {
  const navigate = useNavigate();
  const { data: session } = useSession();
  const { data: audits, isLoading, isError, refetch } = useAudits();
  const deleteAudit = useDeleteAudit();
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AuditListItem | null>(null);

  const role = session?.user?.role ?? "";
  const isAuditor = role === "compliance_auditor";
  const canCreate = showCreateButton && (role === "admin" || role === "compliance_manager");
  const canDelete = role === "admin" || role === "compliance_manager";
  const canSeeStale = role === "admin" || role === "compliance_manager";

  const columns: ResizableColumn[] = [
    { key: "audit", defaultWidth: 260 },
    { key: "startedOn", defaultWidth: 130 },
    { key: "lastUpdated", defaultWidth: 130 },
    { key: "template", defaultWidth: 280 },
    { key: "progress", defaultWidth: 170 },
    ...(!isAuditor ? [{ key: "assignee", defaultWidth: 150 }] : []),
    { key: "status", defaultWidth: 120 },
    ...(canDelete ? [{ key: "actions", defaultWidth: 48, minWidth: 48 }] : []),
  ];
  const { widths, startResize } = useResizableColumns("audits-list", columns);

  if (isError) {
    return <ErrorState message="Failed to load audits" onRetry={() => refetch()} />;
  }

  const displayAudits = (() => {
    const q = searchQuery.trim().toLowerCase();
    return (audits ?? [])
      .filter((a) => matchesStatusFilter(a.status, statusFilter))
      .filter(
        (a) =>
          !q ||
          a.projectName.toLowerCase().includes(q) ||
          (a.accountName ?? "").toLowerCase().includes(q) ||
          a.templates.some((t) => t.name.toLowerCase().includes(q)) ||
          (a.assignedToName ?? "").toLowerCase().includes(q),
      );
  })();

  return (
    <>
      <div className="space-y-4">
        {canCreate && (
          <div className="flex justify-end">
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />
              New Audit
            </Button>
          </div>
        )}

        {isLoading ? (
          <div className="rounded-md border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  {["Audit", "Started On", "Last Updated", "Template", "Progress", "Status"].map(
                    (h) => (
                      <th key={h} className="px-4 py-3 text-left font-medium text-muted-foreground">
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {["sk-r1", "sk-r2", "sk-r3"].map((rk) => (
                  <tr key={rk} className="border-b border-border last:border-0">
                    {["sk-c1", "sk-c2", "sk-c3", "sk-c4", "sk-c5", "sk-c6"].map((ck) => (
                      <td key={ck} className="px-4 py-3">
                        <div className="h-4 bg-muted rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : displayAudits.length === 0 ? (
          searchQuery.trim() ? (
            <EmptyState
              icon={ClipboardCheck}
              title={`No audits match "${searchQuery.trim()}"`}
              description="Try a different search term."
            />
          ) : (
            <EmptyState
              icon={ClipboardCheck}
              title={isAuditor ? "No audits assigned to you" : "No audits yet"}
              description={
                canCreate
                  ? "Create your first audit to get started."
                  : "No audits have been started yet."
              }
            />
          )
        ) : (
          <div className="rounded-md border border-border overflow-x-auto">
            <table className="w-full table-fixed text-sm">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <ResizableTh width={widths.audit} onResizeStart={startResize("audit")}>
                    Audit
                  </ResizableTh>
                  <ResizableTh
                    width={widths.startedOn}
                    onResizeStart={startResize("startedOn")}
                    className="hidden md:table-cell"
                  >
                    Started On
                  </ResizableTh>
                  <ResizableTh
                    width={widths.lastUpdated}
                    onResizeStart={startResize("lastUpdated")}
                    className="hidden md:table-cell"
                  >
                    Last Updated
                  </ResizableTh>
                  <ResizableTh
                    width={widths.template}
                    onResizeStart={startResize("template")}
                    className="hidden lg:table-cell"
                  >
                    Template
                  </ResizableTh>
                  <ResizableTh width={widths.progress} onResizeStart={startResize("progress")}>
                    Progress
                  </ResizableTh>
                  {!isAuditor && (
                    <ResizableTh
                      width={widths.assignee}
                      onResizeStart={startResize("assignee")}
                      className="hidden md:table-cell"
                    >
                      Assignee
                    </ResizableTh>
                  )}
                  <ResizableTh width={widths.status} onResizeStart={startResize("status")}>
                    Status
                  </ResizableTh>
                  {canDelete && (
                    <ResizableTh width={widths.actions} onResizeStart={startResize("actions")} />
                  )}
                </tr>
              </thead>
              <tbody>
                {displayAudits.map((audit) => (
                  <tr
                    key={audit.id}
                    className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer"
                    onClick={() =>
                      navigate({
                        to: "/dashboard/audits/$auditId",
                        params: { auditId: audit.id },
                      })
                    }
                  >
                    <td className="px-4 py-3 font-medium">
                      {audit.projectName}
                      {canSeeStale && audit.isStale && <StaleAuditFlag idleDays={audit.idleDays} />}
                    </td>
                    <td className="hidden md:table-cell px-4 py-3 text-muted-foreground">
                      {formatDate(audit.createdAt)}
                    </td>
                    <td className="hidden md:table-cell px-4 py-3 text-muted-foreground">
                      {formatDate(audit.updatedAt)}
                    </td>
                    <td className="hidden lg:table-cell px-4 py-3 text-muted-foreground">
                      {audit.templates.map((t) => t.name).join(", ") || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <ProgressBar checked={audit.checkedItems} total={audit.totalItems} />
                    </td>
                    {!isAuditor && (
                      <td className="hidden md:table-cell px-4 py-3 text-muted-foreground">
                        {audit.assignedToName ?? "—"}
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <Badge variant={AUDIT_STATUS_BADGE_VARIANTS[audit.status] ?? "outline"}>
                        {AUDIT_STATUS_LABELS[audit.status] ?? audit.status}
                      </Badge>
                    </td>
                    {canDelete && (
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget(audit);
                          }}
                          className="text-muted-foreground hover:text-destructive transition-colors"
                          title="Delete audit"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CreateAuditDialog open={createOpen} onOpenChange={setCreateOpen} />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title="Delete Audit"
        description={`Are you sure you want to delete the audit for "${deleteTarget?.projectName}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        loading={deleteAudit.isPending}
        onConfirm={async () => {
          if (!deleteTarget) return;
          await deleteAudit.mutateAsync(deleteTarget.id);
          setDeleteTarget(null);
        }}
      />
    </>
  );
}
